import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  isRequestAborted,
  shouldReportBannerError,
  shouldReportConsoleError,
} from "@/api/api-utils.js";

/**
 * @typedef {Object} ErrorItem
 * @property {string} id - Unique identifier for the error
 * @property {string} message - Error message
 * @property {string} [details] - Additional error details
 * @property {Date} timestamp - When the error occurred
 */

/**
 * @typedef {Object} ErrorContextType
 * @property {ErrorItem[]} errors - Array of error items
 * @property {(message: string, details?: string, error?: unknown) => void} addError - Function to add an error
 * @property {(id: string) => void} removeError - Function to remove an error by ID
 * @property {() => void} clearAllErrors - Function to clear all errors
 */

const ErrorContext = createContext(/** @type {ErrorContextType|null} */ (null));

/**
 * Hook to use the error context
 * @returns {ErrorContextType} Error context value
 * @throws {Error} If used outside of ErrorProvider
 */
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
};

/**
 * Error Provider component that manages global error state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} [props.captureConsoleErrors=true] - Whether to capture console.error calls
 * @returns {JSX.Element} Error provider component
 */
export function ErrorProvider({ children, captureConsoleErrors = true }) {
  const [errors, setErrors] = useState(/** @type {ErrorItem[]} */ ([]));

  /**
   * Pushes an error into context without applying reporting filters.
   * @param {string} message - Error message
   * @param {string} [details] - Additional error details
   */
  const pushError = useCallback((message, details) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const errorItem = {
      id,
      message: message || "An unknown error occurred",
      details,
      timestamp: new Date(),
    };

    setErrors((prev) => {
      // Limit to 5 errors max to prevent UI clutter
      const newErrors = [errorItem, ...prev].slice(0, 5);
      return newErrors;
    });
  }, []);

  /**
   * Add an error to the error list
   * @param {string} message - Error message
   * @param {string} [details] - Additional error details
   * @param {unknown} [error] - Optional raw error object for richer filtering
   */
  const addError = useCallback((message, details, error) => {
    if (!shouldReportBannerError(message, details, error)) return;
    pushError(message, details);
  }, [pushError]);

  /**
   * Remove an error by ID
   * @param {string} id - Error ID to remove
   */
  const removeError = useCallback((id) => {
    setErrors((prev) => prev.filter((error) => error.id !== id));
  }, []);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Intercept console.error to capture errors
  useEffect(() => {
    if (!captureConsoleErrors) return;

    // Store the original console.error
    const originalConsoleError = console.error;

    // Override console.error
    console.error = (...args) => {
      if (args.some((arg) => isRequestAborted(arg))) {
        originalConsoleError.apply(console, args);
        return;
      }

      // Call the original console.error first
      originalConsoleError.apply(console, args);

      // Extract error message from arguments
      let message = "An error occurred";
      let details = null;

      if (args.length > 0) {
        const firstArg = args[0];

        if (typeof firstArg === "string") {
          message = firstArg;
          // If there are more args, use them as details
          if (args.length > 1) {
            details = args
              .slice(1)
              .map((arg) => {
                if (arg instanceof Error) {
                  return arg.message || arg.toString();
                }
                if (typeof arg === "object") {
                  try {
                    return JSON.stringify(arg, null, 2);
                  } catch {
                    return String(arg);
                  }
                }
                return String(arg);
              })
              .join(" ");
          }
        } else if (firstArg instanceof Error) {
          message = firstArg.message || firstArg.toString();
          if (args.length > 1) {
            details = args
              .slice(1)
              .map((arg) => String(arg))
              .join(" ");
          }
        } else if (typeof firstArg === "object") {
          try {
            message = JSON.stringify(firstArg, null, 2);
          } catch {
            message = String(firstArg);
          }
        } else {
          message = String(firstArg);
        }
      }

      // Ignore warnings - check if message contains "warning" or "warn" (case-insensitive)
      const messageLower = message.toLowerCase();
      const detailsLower = details?.toLowerCase() || "";
      if (
        messageLower.includes("warning")
        || messageLower.includes("warn")
        || detailsLower.includes("warning")
        || detailsLower.includes("warn")
      ) {
        return; // Don't capture warnings
      }

      if (!shouldReportConsoleError(message, details, args)) {
        return;
      }

      // Add error to context
      pushError(message, details);
    };

    // Cleanup: restore original console.error on unmount
    return () => {
      console.error = originalConsoleError;
    };
  }, [captureConsoleErrors, pushError]);

  /** @type {ErrorContextType} */
  const value = {
    errors,
    addError,
    removeError,
    clearAllErrors,
  };

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
}
